import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Head from 'expo-router/head';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { ArticlesSection } from '@/components/riviera/articles-section';
import { BoatsCarousel } from '@/components/riviera/boats-carousel';
import { CategoryNav } from '@/components/riviera/category-nav';
import { EventsCarousel } from '@/components/riviera/events-carousel';
import { ExperiencesCarousel } from '@/components/riviera/experiences-carousel';
import { FeatureStrip } from '@/components/riviera/feature-strip';
import { FiltersBar } from '@/components/riviera/filters-bar';
import { HeroSearch } from '@/components/riviera/hero-search';
import { MainContent } from '@/components/riviera/main-content';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { MaxContentWidth, SidebarWidth, Spacing } from '@/constants/theme';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { FiltersProvider } from '@/hooks/use-filters';
import { useTheme } from '@/hooks/use-theme';
import { useLocale } from '@todo/core/i18n/use-locale';
import { articlesIndexSeedKey } from '@todo/core/list-queries';
import { SITE_URL, absoluteUrl, hreflangLinks } from '@todo/core/seo';

export default function HomeScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { isLg } = useBreakpoint();

  const canonical = absoluteUrl(locale);

  return (
    <FiltersProvider>
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        {/* key forces a fresh mount on locale change — see the comment in
            app/[lang]/_layout.tsx for why. */}
        <Head key={locale}>
          <title>{t('seo.homeTitle')}</title>
          <meta name="description" content={t('seo.homeDescription')} />
          <link rel="canonical" href={canonical} />
          {hreflangLinks().map((link) => (
            <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
          ))}
          <meta property="og:type" content="website" />
          <meta property="og:title" content={t('seo.homeTitle')} />
          <meta property="og:description" content={t('seo.homeDescription')} />
          <meta property="og:url" content={canonical} />
          {/* Per-locale 1200x630 share card (public/og-home-*.jpg) — the
              size summary_large_image cards and messenger previews expect. */}
          <meta property="og:image" content={`${SITE_URL}/og-home-${locale}.jpg`} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={t('seo.homeImageAlt')} />
          <meta name="twitter:title" content={t('seo.homeTitle')} />
          <meta name="twitter:description" content={t('seo.homeDescription')} />
          <meta name="twitter:image" content={`${SITE_URL}/og-home-${locale}.jpg`} />
          <meta name="twitter:image:alt" content={t('seo.homeImageAlt')} />
          {/* Baked rows so the client's first render of the articles carousel
              matches this static HTML — see list-prefetch.ts. */}
          {listSeedScript(articlesIndexSeedKey(locale))}
        </Head>
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}>
          {isLg ? (
            <View style={styles.centered}>
              <View style={styles.desktopContainer}>
                <View style={styles.sidebar}>
                  <FiltersBar />
                </View>
                <View style={styles.mainColumn}>
                  <HeroSearch />
                  <CategoryNav />
                  <EventsCarousel />
                  <ExperiencesCarousel />
                  <BoatsCarousel />
                  <ArticlesSection />
                  <FeatureStrip />
                  <MainContent />
                </View>
              </View>
            </View>
          ) : (
            <View style={styles.mobileContent}>
              <HeroSearch />
              <FiltersBar />
              <CategoryNav />
              <EventsCarousel />
              <ExperiencesCarousel />
              <BoatsCarousel />
              <ArticlesSection />
              <FeatureStrip />
              <MainContent />
            </View>
          )}
          <SiteFooter />
        </ScrollView>
      </View>
    </FiltersProvider>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  desktopContainer: {
    flexDirection: 'row',
    gap: Spacing.five,
    maxWidth: MaxContentWidth,
    width: '100%',
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.four,
  },
  sidebar: {
    width: SidebarWidth,
    flexShrink: 0,
  },
  mainColumn: {
    flex: 1,
    minWidth: 0,
    gap: Spacing.five,
  },
  mobileContent: {
    gap: Spacing.four,
    paddingTop: Spacing.three,
  },
});
